<template>
  <div class="container print-resident-account">
    <ErrorsMessages :errors="errors" />
    <b-overlay :show="loading" opacity="0.99">
      <div v-show="data.length === 0" class="v-clean p-5">
        <h6 class="text-center upper-text worksansr">
          LANÇAMENTO NÃO ENCONTRADO!
        </h6>
      </div>
      <div v-if="data.length > 0">
        <div class="mb-5 content-btn-print d-flex justify-content-end">
          <v-btn class="btn-print" @click="printOut">
            <i class="fas fa-print mr-2 text-twenty"></i> IMPRIMIR
          </v-btn>
        </div>

        <div class="invoice-document bg-white">
          <!-- Header da Fatura -->
          <div class="invoice-header">
            <div class="row align-items-start">
              <!-- Logo da Empresa -->
              <div class="col-xl-3 col-lg-3 col-md-3 col-sm-12 col-12">
                <div class="company-logo">
                  <img
                    v-if="rest_home_logo_url"
                    class="rest_home_logo_url"
                    :src="rest_home_logo_url"
                    alt="Logo"
                  />
                  <div v-if="!rest_home_logo_url" class="company-name">
                    <strong class="text-uppercase">{{ rest_home_name }}</strong>
                  </div>
                </div>
              </div>

              <!-- Informações da Empresa -->
              <div class="col-xl-6 col-lg-6 col-md-6 col-sm-12 col-12">
                <div class="company-info">
                  <h5 class="company-title">
                    {{ rest_home_fantasy_name || rest_home_name }}
                  </h5>
                  <div class="company-details">
                    <p class="mb-1">{{ getCompanyAddress() }}</p>
                    <p class="mb-1">{{ getCompanyPhone() }}</p>
                    <p class="mb-1">
                      CNPJ: {{ getCompanyCNPJ() }}
                      <span class="ml-3">IE: {{ getCompanyIE() }}</span>
                    </p>
                    <p class="mb-1">{{ getCompanyLegalName() }}</p>
                  </div>
                </div>
              </div>

              <!-- Data e Número da Fatura -->
              <div class="col-xl-3 col-lg-3 col-md-3 col-sm-12 col-12">
                <div class="invoice-meta text-right">
                  <p class="invoice-date mb-1">
                    {{ moment().format("DD/MM/YYYY") }}
                  </p>
                  <p class="invoice-type mb-0">{{ getInvoiceTitle() }}</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Separador -->
          <hr class="header-divider" />

          <!-- Informações do Cliente/Residente -->
          <div class="client-info" v-if="resident">
            <div class="row">
              <div class="col-12">
                <h6 class="client-name">{{ resident.name }}</h6>
                <div class="client-details">
                  <p class="mb-1">
                    <span v-if="resident.cpf">CPF: {{ resident.cpf }}</span>
                    <span v-if="resident.rg" class="ml-4"
                      >RG: {{ resident.rg }}</span
                    >
                    <span v-if="!resident.cpf && !resident.rg"
                      >CPF: 000.000.000-00 RG: 00000000</span
                    >
                  </p>
                  <p class="mb-1">
                    {{ getResidentAddress(resident) }}
                  </p>
                  <div class="client-dates">
                    <span class="mr-4">
                      Data de nascimento:
                      {{ formatDate(resident.date_of_birth, false) }} ({{
                        getAge(resident.date_of_birth)
                      }}
                      anos)
                    </span>
                    <span v-if="resident.date_of_reception">
                      Data de admissão:
                      {{ formatDate(resident.date_of_reception, false) }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Separador -->
          <hr class="section-divider" />

          <!-- Informações do Lançamento -->
          <div class="launch-info" v-if="shouldShowLaunchInfo()">
            <div class="info-header">
              <h6 class="info-title">
                <i class="fas fa-info-circle mr-2"></i>
                INFORMAÇÕES DO LANÇAMENTO
              </h6>
            </div>
            <div class="row">
              <div class="col-md-4">
                <div class="info-item">
                  <small class="info-label">Conta</small>
                  <p class="info-value">
                    {{ data[0].finance_configuration_account_name }}
                  </p>
                </div>
              </div>
              <div class="col-md-4">
                <div class="info-item">
                  <small class="info-label">Competência</small>
                  <p class="info-value">
                    {{ moment(data[0].competence).format("DD/MM/YYYY") }}
                  </p>
                </div>
              </div>
              <div class="col-md-4">
                <div class="info-item">
                  <small class="info-label">Vencimento</small>
                  <p class="info-value">
                    {{ moment(data[0].due_date).format("DD/MM/YYYY") }}
                  </p>
                </div>
              </div>
              <div class="col-md-12">
                <div class="creation-info">
                  <span class="creation-label mr-2">Criado em:</span>
                  <span class="creation-value">{{
                    moment(data[0].created_at).format("DD/MM/YYYY HH:mm:ss")
                  }}</span>
                  <span class="creation-label ml-3 mr-2">por:</span>
                  <span class="creation-value">{{ getCreatedBy() }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Separador -->
          <hr class="section-divider" v-if="shouldShowLaunchInfo()" />

          <!-- Tabela de Itens -->
          <div class="invoice-table">
            <table class="table table-bordered">
              <thead class="table-header">
                <tr>
                  <th style="width: 15%">VENCIMENTO</th>
                  <th style="width: 15%">PAGO</th>
                  <th style="width: 20%">CATEGORIA</th>
                  <th style="width: 35%">DESCRIÇÃO</th>
                  <th class="text-right" style="width: 10%">VALOR</th>
                  <th class="text-right" style="width: 5%">STATUS</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, index) in data" :key="index">
                  <!-- Vencimento -->
                  <td>
                    <span v-if="!isProductEntry()">{{ item.date }}</span>
                    <span v-else>--</span>
                  </td>

                  <!-- Pago -->
                  <td>
                    <span v-if="!isProductEntry()">
                      {{ item.is_paid == 1 ? item.paid_in_date : "--" }}
                    </span>
                    <span v-else>--</span>
                  </td>

                  <!-- Categoria -->
                  <td>
                    <div v-if="!isProductEntry()">
                      <span
                        class="category-badge"
                        :style="getCategoryStyle(item)"
                      >
                        {{ item.finance_configuration_category_name }}
                      </span>
                    </div>
                    <span v-else>--</span>
                  </td>

                  <!-- Descrição -->
                  <td>
                    <div v-if="!isProductEntry()">
                      <div class="description-content">
                        <strong class="description-title">{{
                          item.description
                        }}</strong>
                      </div>
                    </div>
                    <div v-else-if="item.entity">
                      <div class="product-details">
                        <div class="product-header">
                          <strong
                            >{{ type }}:
                            {{ item.entity.medicine_product_name }}</strong
                          >
                        </div>
                        <div class="product-info">
                          <div class="row">
                            <div class="col-6">
                              <small
                                ><strong>Grupo:</strong>
                                {{ item.entity.stock_group_name }}</small
                              >
                            </div>
                            <div class="col-6">
                              <small
                                ><strong>Quantidade:</strong>
                                {{ item.entity.amount }}</small
                              >
                            </div>
                            <div class="col-6">
                              <small
                                ><strong>Lote:</strong>
                                {{ item.entity.lot }}</small
                              >
                            </div>
                            <div class="col-6">
                              <small
                                ><strong>Entrada:</strong>
                                {{ item.entity.entry_date }}</small
                              >
                            </div>
                            <div class="col-12">
                              <small
                                ><strong>Vencimento:</strong>
                                {{ item.entity.due_date }}</small
                              >
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </td>

                  <!-- Valor -->
                  <td class="text-right">
                    <div v-if="!isProductEntry()">
                      <span :class="getValueClass(item)">
                        {{ getFormattedValue(item) }}
                      </span>
                    </div>
                    <div v-else>
                      <span class="text-success">{{
                        formatMoney(String(0))
                      }}</span>
                    </div>
                  </td>

                  <!-- Status -->
                  <td class="text-right">
                    <div v-if="!isProductEntry()">
                      <span
                        v-if="item.is_paid == 0"
                        class="status-badge status-pending"
                      >
                        <i class="fas fa-clock mr-1"></i>
                        {{ item.late_payment ? "ATRASADO" : "PAGAR" }}
                      </span>
                      <span v-else class="status-badge status-paid">
                        <i class="fas fa-check mr-1"></i>
                        PAGO
                      </span>
                    </div>
                    <div v-else>
                      <span class="status-badge status-family">FAMÍLIA</span>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Totais -->
          <div class="invoice-totals">
            <div class="row">
              <div class="col-xl-8 col-lg-8 col-md-6"></div>
              <div class="col-xl-4 col-lg-4 col-md-6">
                <table class="totals-table">
                  <tr class="total-final">
                    <td class="text-right"><strong>TOTAL</strong></td>
                    <td class="text-right total-value">
                      <strong>{{ formatMoney(String(totalValue)) }}</strong>
                    </td>
                  </tr>
                </table>
              </div>
            </div>
          </div>

          <!-- Condições de Pagamento (se aplicável) -->
          <div class="payment-conditions" v-if="!isProductEntry()">
            <div class="row">
              <div class="col-12">
                <h6><strong>Condição de pagamento:</strong></h6>
                <p><strong>Forma de pagamento:</strong> Boleto Bancário</p>

                <table class="payment-table table table-bordered mt-3">
                  <thead>
                    <tr>
                      <th>Nº</th>
                      <th>Vencimento</th>
                      <th class="text-right">Valor (R$)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>1º</td>
                      <td>{{ getPaymentDate() }}</td>
                      <td class="text-right">
                        {{ formatMoney(String(totalValue)) }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <div class="mt-5 content-btn-print d-flex justify-content-end">
          <v-btn class="btn-print" @click="printOut">
            <i class="fas fa-print mr-2 text-twenty"></i> IMPRIMIR
          </v-btn>
        </div>
      </div>
    </b-overlay>
  </div>
</template>

<script>
import ErrorsMessages from "../../Components/ErrorsMessages";

import api from "../../../services/api";

import routes from "../../../routes";

import { userData } from "../../../lib/functions";

import { getAge } from "../../../lib/getAgeByDate";

import {
  formatDate,
  formatMoney,
  professionalClassCouncilValue,
} from "../../../lib/format";

import moment from "moment";

export default {
  name: "ResidentAccountPrint",

  components: {
    ErrorsMessages,
  },

  data() {
    return {
      loading: false,

      resident_id: "",
      id: "",
      type: "",
      origin: "",
      movement: "",

      errors: [],

      data: [],
      resident: null,

      options: {
        page: 1,
      },

      totalValue: 0,

      user: {},
      rest_home_name: "",
      rest_home_fantasy_name: "",
      rest_home_logo_url: null,
      logo: null,
    };
  },

  created() {
    this.retrieve();
  },

  methods: {
    moment,
    getAge,
    formatDate,
    formatMoney,
    professionalClassCouncilValue,

    // Métodos helper para o template
    getCompanyAddress() {
      return this.user.rest_home_address || "--";
    },

    getCompanyPhone() {
      return this.user.rest_home_first_phone || "--";
    },

    getCompanyCNPJ() {
      return this.user.rest_home_cnpj || "--";
    },

    getCompanyIE() {
      return this.user.rest_home_ie || "--";
    },

    getCompanyLegalName() {
      return this.user.rest_home_fantasy_name;
    },

    getInvoiceTitle() {
      return "Lançamento " + (this.id || "--");
    },

    getResidentAddress(resident) {
      let address = resident.address || "--";
      if (resident.city) {
        address += " - " + resident.city;
      }
      if (resident.uf_initials) {
        address += " - " + resident.uf_initials;
      }
      if (resident.cep) {
        address += " - CEP: " + resident.cep;
      }
      return address;
    },

    shouldShowLaunchInfo() {
      return (
        (this.type != "PRODUTO" && this.type != "MEDICAMENTO") ||
        this.movement != "ENTRADA"
      );
    },

    isProductEntry() {
      return (
        (this.type == "PRODUTO" || this.type == "MEDICAMENTO") &&
        this.movement == "ENTRADA"
      );
    },

    getCreatedBy() {
      if (this.data.length > 0) {
        return professionalClassCouncilValue({
          id: this.data[0].user_professional_class_council_id,
          name: this.data[0].user_name,
          profile: this.data[0].user_profile_name,
          cpf: this.data[0].user_cpf,
          office: this.data[0].user_office,
          value: this.data[0].user_professional_class_council_value,
        });
      }
      return "";
    },

    getCategoryStyle(item) {
      const color = item.finance_configuration_category_color || "#999";
      const textColor = color.toUpperCase() === "#FFFFFF" ? "#333" : "#fff";
      return `background-color: ${color}; color: ${textColor};`;
    },

    getValueClass(item) {
      return item.color === "danger" ? "text-danger" : "text-success";
    },

    getFormattedValue(item) {
      const prefix = item.color === "danger" ? "-" : "";
      const value = String(item.value || 0).replace("-", "");
      return prefix + this.formatMoney(value);
    },

    getPaymentDate() {
      if (this.data.length > 0 && this.data[0].due_date) {
        return moment(this.data[0].due_date).format("DD/MM/YYYY");
      }
      return moment().format("DD/MM/YYYY");
    },

    getSignature() {
      return professionalClassCouncilValue({
        id: this.user.professional_class_council_id,
        name: this.user.name,
        profile: this.user.profile_name,
        cpf: this.user.cpf,
        office: this.user.office,
        value: this.user.professional_class_council_value,
      });
    },

    getCurrentDateTime() {
      return moment().format("DD/MM/YYYY HH:mm:ss");
    },

    retrieve() {
      const { resident_id, type, origin, movement, id } =
        routes.history.current.params;

      if (!resident_id || !type || !id) {
        this.errors = ["Lançamento não encontrado"];
        return;
      }

      this.resident_id = resident_id;
      this.type = type;
      this.origin = origin;
      this.movement = movement;
      this.id = id;

      const { user, logo } = userData();

      this.user = user;
      this.rest_home_name = user.rest_home_name;
      this.rest_home_fantasy_name = user.rest_home_fantasy_name;
      this.rest_home_logo_url = user.logo_url;
      this.logo = logo;

      this.get();
    },

    async get() {
      try {
        this.loading = true;

        const { resident_id, type, origin, movement, id } = this;

        let params = { resident_id, type, origin, movement, id };

        const response = await api.get("/rest-home/resident/account/print", {
          params,
        });

        const { data, resident, error, errors } = response.data;

        if (error) {
          this.errors = errors;
        } else {
          let totalValue = 0;
          for (let item of data) {
            if (item.total) {
              totalValue = parseFloat(totalValue) + parseFloat(item.total);
            }
          }
          this.data = data;
          this.resident = resident;
          this.totalValue = totalValue;
          this.errors = [];
        }

        this.loading = false;
      } catch (error) {
        console.log(error);
      }
    },

    printOut() {
      window.print();
    },
  },
};
</script>

<style scoped>
/* Estilos gerais para impressão */
.print-resident-account {
  font-family: "WorkSans-Regular", Arial, sans-serif;
  color: #333;
  background-color: #fff;
}

.invoice-document {
  max-width: 100%;
  margin: 0 auto;
  padding: 20px;
  border: 1px solid #ddd;
  background-color: #fff;
  min-height: 100vh;
}

/* Header da fatura */
.invoice-header {
  margin-bottom: 20px;
}

.company-logo {
  display: flex;
  align-items: center;
  height: 80px;
}

.rest_home_logo_url {
  max-height: 80px;
  max-width: 100%;
  object-fit: contain;
}

.company-name {
  font-size: 18px;
  font-weight: bold;
}

.company-info {
  text-align: left;
}

.company-title {
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 10px;
  color: #333;
}

.company-details p {
  font-size: 13px;
  margin-bottom: 3px;
  color: #333;
  line-height: 1.3;
}

.invoice-meta {
  text-align: right;
}

.invoice-date {
  font-size: 14px;
  color: #666;
}

.invoice-type {
  font-size: 18px;
  font-weight: bold;
  color: #333;
}

/* Separadores */
.header-divider {
  border: none;
  border-top: 2px solid #333;
  margin: 20px 0;
}

.section-divider {
  border: none;
  border-top: 1px solid #ddd;
  margin: 15px 0;
}

/* Informações do cliente */
.client-info {
  margin-bottom: 20px;
  padding: 15px;
  border: 2px solid #000;
  background-color: #fff;
}

.client-name {
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 10px;
  color: #333;
}

.client-details p {
  font-size: 13px;
  margin-bottom: 4px;
  color: #333;
  line-height: 1.4;
}

.client-dates {
  font-size: 13px;
  color: #666;
  margin-top: 8px;
}

/* Informações do lançamento */
.launch-info {
  margin-bottom: 20px;
  padding: 15px;
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
}

.info-header {
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 2px solid #e5e7eb;
}

.info-title {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
  display: flex;
  align-items: center;
}

.info-title i {
  color: #0f65b0;
}

.info-item {
  margin-bottom: 10px;
}

.info-label {
  font-size: 12px;
  color: #666;
  text-transform: uppercase;
  font-weight: 600;
  display: block;
}

.info-value {
  font-size: 14px;
  color: #333;
  font-weight: 500;
  margin: 0;
  margin-top: 2px;
}

.creation-info {
  background: white;
  padding: 10px;
  border-radius: 6px;
  font-size: 13px;
}

.creation-label {
  color: #666;
  font-weight: 600;
}

.creation-value {
  color: #333;
  font-weight: 500;
}

/* Tabela de itens */
.invoice-table {
  margin-bottom: 20px;
}

.table-header th {
  background-color: #f8f9fa;
  font-weight: bold;
  font-size: 14px;
  padding: 12px 8px;
  border: 1px solid #dee2e6;
  color: #333;
}

.table tbody td {
  padding: 12px 8px;
  border: 1px solid #dee2e6;
  font-size: 14px;
  vertical-align: top;
}

.category-badge {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  text-transform: uppercase;
}

.description-content {
  color: #333;
}

.description-title {
  font-size: 14px;
  color: #333;
}

.product-details {
  background: #f8f9fa;
  padding: 10px;
  border-radius: 6px;
}

.product-header {
  margin-bottom: 8px;
  padding-bottom: 6px;
  border-bottom: 1px solid #e5e7eb;
}

.product-info small {
  font-size: 12px;
  color: #666;
  line-height: 1.4;
}

/* Status badges */
.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
}

.status-pending {
  background: #fef3c7;
  color: #d97706;
  border: 1px solid #f59e0b;
}

.status-paid {
  background: #d1fae5;
  color: #059669;
  border: 1px solid #34d399;
}

.status-family {
  background: #e0e7ff;
  color: #4338ca;
  border: 1px solid #818cf8;
}

/* Totais */
.invoice-totals {
  margin-bottom: 20px;
}

.totals-table {
  width: 100%;
  border-collapse: collapse;
}

.totals-table td {
  padding: 8px 12px;
  border: 1px solid #dee2e6;
  font-size: 14px;
}

.total-final {
  background-color: #f8f9fa;
}

.total-value {
  font-size: 16px;
}

/* Condições de pagamento */
.payment-conditions {
  margin-bottom: 30px;
}

.payment-conditions h6 {
  margin-bottom: 10px;
  color: #333;
}

.payment-table {
  max-width: 400px;
}

.payment-table th,
.payment-table td {
  padding: 8px 12px;
  font-size: 14px;
  border: 1px solid #dee2e6;
}

.payment-table th {
  background-color: #f8f9fa;
  font-weight: bold;
}

/* Assinatura */
.signature-section {
  margin: 40px 0 20px 0;
  padding-top: 20px;
  border-top: 1px solid #ddd;
}

.signature-section p {
  font-size: 14px;
  color: #666;
}

/* Rodapé da página */
.page-footer {
  text-align: right;
  margin-top: 20px;
  padding-top: 10px;
  border-top: 1px solid #eee;
}

/* Estilos para impressão */
@media print {
  .print-resident-account .content-btn-print {
    display: none !important;
  }

  .invoice-document {
    border: none;
    padding: 15px;
    box-shadow: none;
    max-width: none;
    margin: 0;
  }

  .invoice-header,
  .client-info,
  .launch-info,
  .invoice-table,
  .invoice-totals,
  .payment-conditions,
  .signature-section {
    page-break-inside: avoid;
  }

  .table {
    border-collapse: collapse;
    width: 100%;
  }

  .table th,
  .table td {
    border: 1px solid #000 !important;
    font-size: 12px !important;
    padding: 6px 8px !important;
  }

  .table-header th {
    background-color: #f8f9fa !important;
    -webkit-print-color-adjust: exact;
  }

  .company-title {
    font-size: 16px !important;
  }

  .invoice-type {
    font-size: 16px !important;
    font-weight: bold !important;
  }

  .client-name {
    font-size: 14px !important;
    font-weight: bold !important;
  }

  .totals-table td {
    border: 1px solid #000 !important;
    font-size: 12px !important;
  }

  .payment-table th,
  .payment-table td {
    border: 1px solid #000 !important;
    font-size: 12px !important;
  }

  body {
    -webkit-print-color-adjust: exact;
    color-adjust: exact;
  }

  .header-divider {
    border-top: 1px solid #000 !important;
  }

  .section-divider {
    border-top: 1px solid #000 !important;
  }

  .category-badge,
  .status-badge {
    -webkit-print-color-adjust: exact;
    color-adjust: exact;
  }
}

/* Responsividade */
@media (max-width: 768px) {
  .invoice-meta {
    text-align: left;
    margin-top: 15px;
  }

  .company-info {
    text-align: left;
    margin-top: 15px;
  }

  .totals-table {
    font-size: 12px;
  }

  .payment-table {
    font-size: 12px;
  }

  .launch-info .row .col-md-4 {
    margin-bottom: 10px;
  }
}
</style>